import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import CartDrawer from '../components/common/CartDrawer';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function ProductDetailPage() {
  const { id }         = useParams();
  const navigate       = useNavigate();
  const { addToCart, setCartOpen } = useCart();

  const [product,      setProduct]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [activeImage,  setActiveImage]  = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity,     setQuantity]     = useState(1);
  const [adding,       setAdding]       = useState(false);
  const [activeTab,    setActiveTab]    = useState('specs');

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`)
      .then(r => {
        setProduct(r.data.data);
        if (r.data.data.sizes?.length) setSelectedSize(r.data.data.sizes[0]);
      })
      .catch(() => navigate('/products'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleAddToCart = async () => {
    setAdding(true);
    const ok = await addToCart(
      product._id,
      selectedSize?._id || null,
      selectedSize?.label || '',
      quantity,
      product.name
    );
    if (ok) setCartOpen(true);
    setAdding(false);
  };

  if (loading) return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 pt-20 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-container border-t-transparent rounded-full animate-spin" />
          <p className="font-label text-sm text-secondary uppercase tracking-widest">Loading product...</p>
        </div>
      </div>
    </div>
  );

  if (!product) return null;

  const images = product.images?.length ? product.images : [];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <CartDrawer />

      <main className="flex-1 pt-20">
        {/* Breadcrumb */}
        <div className="bg-surface-container-low border-b border-outline-variant/20 px-6 md:px-10">
          <div className="max-w-screen-2xl mx-auto py-3 flex items-center gap-2 font-label text-xs text-secondary uppercase tracking-widest flex-wrap">
            <Link to="/" className="hover:text-primary-container transition-colors">Home</Link>
            <span>/</span>
            <Link to="/products" className="hover:text-primary-container transition-colors">Products</Link>
            {product.category && <>
              <span>/</span>
              <Link to={`/products?category=${product.category._id}`} className="hover:text-primary-container transition-colors">
                {product.category.name}
              </Link>
            </>}
            {product.subcategory && <>
              <span>/</span>
              <Link to={`/products?category=${product.category?._id}&subcategory=${product.subcategory._id}`}
                className="hover:text-primary-container transition-colors">
                {product.subcategory.name}
              </Link>
            </>}
            <span>/</span>
            <span className="text-on-surface font-bold truncate max-w-[200px]">{product.name}</span>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-6 md:px-10 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-20">

            {/* ── Image Gallery ───────────────────────────────────────── */}
            <div className="space-y-4">
              <div className="aspect-square bg-surface-container overflow-hidden border border-outline-variant/20">
                {images.length > 0
                  ? <img src={images[activeImage]} alt={product.name}
                      className="w-full h-full object-contain p-8" />
                  : <div className="w-full h-full flex items-center justify-center">
                      <span className="material-symbols-outlined text-[120px] text-outline-variant">bolt</span>
                    </div>
                }
              </div>
              {images.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {images.map((img, i) => (
                    <button key={i} onClick={() => setActiveImage(i)}
                      className={`flex-shrink-0 w-20 h-20 border-2 overflow-hidden transition-colors ${
                        activeImage === i ? 'border-primary-container' : 'border-outline-variant/30 hover:border-outline-variant'
                      }`}>
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ── Product Info ────────────────────────────────────────── */}
            <div className="space-y-6">
              {/* Header */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  {product.category && (
                    <Link to={`/products?category=${product.category._id}`}
                      className="font-label text-xs uppercase tracking-widest text-secondary hover:text-primary-container transition-colors">
                      {product.category.name}
                    </Link>
                  )}
                  {product.grade && <span className="badge badge-orange">{product.grade}</span>}
                </div>
                <h1 className="font-headline font-black text-4xl md:text-5xl uppercase leading-tight tracking-tight text-on-surface">
                  {product.name}
                </h1>
                {product.series && <p className="font-label text-sm text-secondary mt-1">Series: {product.series}</p>}
              </div>

              {/* Meta pills */}
              <div className="flex flex-wrap gap-2">
                {product.material && (
                  <span className="flex items-center gap-1.5 bg-surface-container px-3 py-1.5 font-label text-xs text-secondary">
                    <span className="material-symbols-outlined text-[14px]">category</span>
                    {product.material}
                  </span>
                )}
                {product.finish && (
                  <span className="flex items-center gap-1.5 bg-surface-container px-3 py-1.5 font-label text-xs text-secondary">
                    <span className="material-symbols-outlined text-[14px]">palette</span>
                    {product.finish}
                  </span>
                )}
              </div>

              {/* Description */}
              {product.description && (
                <p className="font-body text-sm text-secondary leading-relaxed">{product.description}</p>
              )}

              {/* Size selector */}
              {product.sizes?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="input-label mb-0">Select Size</label>
                    {selectedSize && <span className="font-label text-xs text-primary-container font-bold">{selectedSize.label}</span>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {product.sizes.map(size => (
                      <button key={size._id} onClick={() => setSelectedSize(size)}
                        className={`px-4 py-2 border font-label text-sm font-bold transition-colors ${
                          selectedSize?._id === size._id
                            ? 'border-primary-container bg-primary-container text-white'
                            : 'border-outline-variant text-on-surface hover:border-primary-container hover:text-primary-container'
                        }`}>
                        {size.label}
                        {size.sku && <span className="ml-1 opacity-60 text-[10px]">({size.sku})</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity */}
              <div>
                <label className="input-label">Quantity</label>
                <div className="flex items-center gap-3">
                  <div className="flex items-center border border-outline-variant">
                    <button onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      className="w-11 h-11 flex items-center justify-center text-secondary hover:bg-surface-container transition-colors">
                      <span className="material-symbols-outlined text-[20px]">remove</span>
                    </button>
                    <input type="number" min="1" value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-16 h-11 text-center font-label font-bold text-sm text-on-surface border-x border-outline-variant focus:outline-none bg-white" />
                    <button onClick={() => setQuantity(q => q + 1)}
                      className="w-11 h-11 flex items-center justify-center text-secondary hover:bg-surface-container transition-colors">
                      <span className="material-symbols-outlined text-[20px]">add</span>
                    </button>
                  </div>
                  <span className="font-label text-xs text-secondary">pieces</span>
                </div>
              </div>

              {/* Add to inquiry */}
              <div className="pt-2 space-y-3">
                <button onClick={handleAddToCart} disabled={adding}
                  className="btn-primary w-full justify-center text-base py-4 disabled:opacity-70 disabled:cursor-not-allowed">
                  {adding
                    ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Adding...</>
                    : <><span className="material-symbols-outlined">add_shopping_cart</span> Add to Inquiry List</>
                  }
                </button>
              </div>

              {/* Inquiry note */}
              <div className="flex items-start gap-3 p-4 bg-surface-container-low border-l-4 border-primary-container">
                <span className="material-symbols-outlined text-primary-container text-[20px] mt-0.5 flex-shrink-0">info</span>
                <p className="font-body text-xs text-secondary leading-relaxed">
                  Add to your inquiry list, review your selections, and submit — we'll send you a confirmation with your inquiry number and PDF.
                </p>
              </div>
            </div>
          </div>

          {/* ── Tabs ──────────────────────────────────────────────────── */}
          <div className="mt-16">
            <div className="flex border-b border-outline-variant/30">
              {[
                { key: 'specs',   label: 'Specifications' },
                { key: 'measure', label: 'How to Measure' },
              ].filter(t => t.key !== 'measure' || product.measureGuide?.steps?.length > 0)
               .map(tab => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                  className={`px-6 py-4 font-headline font-bold text-sm uppercase tracking-tight transition-colors border-b-2 -mb-px ${
                    activeTab === tab.key
                      ? 'border-primary-container text-primary-container'
                      : 'border-transparent text-secondary hover:text-on-surface'
                  }`}>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="py-8">
              {/* Specifications */}
              {activeTab === 'specs' && (
                product.specifications?.length > 0 ? (
                  <table className="cf-table max-w-2xl">
                    <tbody>
                      {product.specifications.map((spec, i) => (
                        <tr key={i}>
                          <td className="font-label text-xs uppercase tracking-widest text-secondary w-1/3">{spec.label}</td>
                          <td className="font-body text-sm font-semibold text-on-surface">{spec.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="font-body text-sm text-secondary italic">No specifications available.</p>
                )
              )}

              {/* Measure guide */}
              {activeTab === 'measure' && product.measureGuide && (
                <div className="max-w-3xl space-y-6">
                  {product.measureGuide.intro && (
                    <p className="font-body text-sm text-secondary leading-relaxed">{product.measureGuide.intro}</p>
                  )}
                  {product.measureGuide.steps?.map((step, i) => (
                    <div key={i} className="flex gap-6 items-start">
                      <div className="w-8 h-8 bg-primary-container text-white font-headline font-black text-base flex items-center justify-center flex-shrink-0">
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        {step.image && (
                          <img src={step.image} alt={step.caption || `Step ${i + 1}`}
                            className="h-48 object-contain mb-3 border border-outline-variant/20" />
                        )}
                        {step.caption && <p className="font-label text-sm font-bold text-on-surface">{step.caption}</p>}
                        {step.note   && <p className="font-body text-xs text-secondary mt-1">{step.note}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
